import { supabase } from "../../../lib/supabase";

export interface Appointment {
  id: string;
  menteeName: string;
  menteeEmail: string;
  dateTime: string;
  duration: number;
  status: "pending" | "confirmed" | "declined" | "ended";
  meetingLink?: string;
  feedback?: string;
}

interface RawMeetingData {
  uuid: string;
  mentee_uuid: string;
  mentor_uuid: string;
  scheduled_time: string;
  duration: number;
  status: "pending" | "confirmed" | "declined" | "ended";
  meeting_link: string | null;
  feedback: string | null;
  mentee: {
    uuid: string;
    vincer_base_user: {
      name: string;
      email: string;
    };
  };
}

/**
 * Fetches all appointments for the current mentor
 */
export async function fetchMentorAppointments() {
  try {
    // Get the current user's ID
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Get the mentor UUID from the mentor table
    const { data: mentorData, error: mentorError } = await supabase
      .from("mentor")
      .select("uuid")
      .eq("user_uuid", user.id)
      .single();

    if (mentorError) {
      console.error("Error fetching mentor data:", mentorError);
      if (mentorError.code === "PGRST116") {
        throw new Error(
          "No mentor record found for this user. Please ensure you are registered as a mentor."
        );
      }
      throw new Error(`Could not find mentor record: ${mentorError.message}`);
    }

    if (!mentorData) {
      throw new Error(
        "No mentor record found for this user. Please ensure you are registered as a mentor."
      );
    }

    // Fetch all meetings for this mentor
    const { data, error } = await supabase
      .from("meeting")
      .select(
        `
        uuid,
        mentee_uuid,
        mentor_uuid,
        scheduled_time,
        duration,
        status,
        meeting_link,
        feedback,
        mentee:mentee_uuid (
          uuid,
          vincer_base_user (
            name,
            email
          )
        )
      `
      )
      .eq("mentor_uuid", mentorData.uuid);

    if (error) {
      console.error("Error fetching appointments:", error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Transform the data to match our Appointment interface
    const appointments: Appointment[] = (
      data as unknown as RawMeetingData[]
    ).map((meeting) => ({
      id: meeting.uuid,
      menteeName: meeting.mentee.vincer_base_user.name,
      menteeEmail: meeting.mentee.vincer_base_user.email,
      dateTime: meeting.scheduled_time,
      duration: meeting.duration,
      status: meeting.status,
      meetingLink: meeting.meeting_link || undefined,
    }));

    return appointments;
  } catch (error) {
    console.error("Error in fetchMentorAppointments:", error);
    throw error;
  }
}

/**
 * Updates the status of an appointment
 */
export async function updateAppointmentStatus(
  appointmentId: string,
  status: "pending" | "confirmed" | "declined"
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from("meeting")
      .update({ status })
      .eq("uuid", appointmentId);

    if (error) {
      console.error("Error updating appointment status:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error("Error in updateAppointmentStatus:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
}

export async function getUserAuthTokenForMeeting(
  meetingId: string,
  role: string
) {
  let authTokenName = "";
  if (role === "mentor") {
    authTokenName = "mentor_auth_token";
  } else {
    authTokenName = "mentee_auth_token";
  }

  const { data, error } = await supabase
    .from("meeting")
    .select(authTokenName)
    .eq("uuid", meetingId)
    .single();

  if (error) {
    console.error("Error fetching auth token:", error);
    throw error;
  }

  // @ts-ignore
  return data[authTokenName];
}

export async function getLastThreePastAppointments() {
  try {
    // Get the current user's ID
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Get the mentor UUID
    const { data: mentorData, error: mentorError } = await supabase
      .from("mentor")
      .select("uuid")
      .eq("user_uuid", user.id)
      .single();

    if (mentorError || !mentorData) {
      throw new Error("No mentor record found for this user");
    }

    // Get current timestamp in ISO format
    const now = new Date().toISOString();

    // Fetch past meetings for this mentor
    const { data, error } = await supabase
      .from("meeting")
      .select(
        `
        uuid,
        mentee_uuid,
        mentor_uuid,
        scheduled_time,
        duration,
        status,
        meeting_link,
        feedback,
        mentee:mentee_uuid (
          uuid,
          vincer_base_user (
            name,
            email
          )
        )
      `
      )
      .eq("mentor_uuid", mentorData.uuid)
      .lt("scheduled_time", now) // Only get past appointments
      .order("scheduled_time", { ascending: false }) // Sort by time descending (most recent first)
      .limit(4); // Get last 4 appointments, 3 past + 1 most recent

    if (error) {
      console.error("Error fetching appointments:", error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Transform the data
    const appointments: Appointment[] = (data as unknown as RawMeetingData[])
      .slice(1)
      .map((meeting) => ({
        id: meeting.uuid,
        menteeName: meeting.mentee.vincer_base_user.name,
        menteeEmail: meeting.mentee.vincer_base_user.email,
        dateTime: meeting.scheduled_time,
        duration: meeting.duration,
        status: meeting.status,
        meetingLink: meeting.meeting_link || undefined,
        feedback: meeting.feedback || undefined,
      }));

    return appointments;
  } catch (error) {
    console.error("Error in fetchLastThreePastAppointments:", error);
    throw error;
  }
}

/**
 * Fetches a single appointment by its ID
 * @param appointmentId The UUID of the appointment to fetch
 * @returns Promise with the appointment data or error
 */
export async function getAppointmentById(appointmentId: string): Promise<{
  success: boolean;
  data?: Appointment;
  error?: string;
}> {
  try {
    // Fetch the specific meeting with related mentee data
    const { data, error } = await supabase
      .from("meeting")
      .select(
        `
        uuid,
        mentee_uuid,
        mentor_uuid,
        scheduled_time,
        duration,
        status,
        meeting_link,
        feedback,
        mentee:mentee_uuid (
          uuid,
          vincer_base_user (
            name,
            email
          )
        )
      `
      )
      .eq("uuid", appointmentId)
      .single();

    if (error) {
      console.error("Error fetching appointment:", error);
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: "Appointment not found" };
    }

    // Transform the data to match our Appointment interface
    const appointment: Appointment = {
      id: data.uuid,
      // @ts-ignore
      menteeName: data.mentee.vincer_base_user.name,
      // @ts-ignore
      menteeEmail: data.mentee.vincer_base_user.email,
      dateTime: data.scheduled_time,
      duration: data.duration,
      status: data.status,
      meetingLink: data.meeting_link || undefined,
      feedback: data.feedback || undefined,
    };

    return { success: true, data: appointment };
  } catch (error) {
    console.error("Error in getAppointmentById:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
}
